<script setup lang="ts">
import { computed, ref } from "vue";
import { useRoute } from "vue-router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";

import {
  addDefectAttachment,
  addDefectComment,
  assignDefect,
  changeDefectStatus,
  draftDefectResolution,
  fetchDefectPdf,
  getDefect,
  getDefectHistory,
  getDownloadUrl,
  getMe,
  getProjectIssues,
  linkDefect,
  listLocations,
  listWorkers,
  removeDefectAttachment,
  resolveDefect,
  reviewDefect,
  unlinkDefect,
  unwatchDefect,
  updateDefect,
  uploadFile,
  watchDefect,
  type RegisterReviewFinding,
} from "@/api/client";
import PageLayout from "@/components/PageLayout.vue";
import PhotoGallery from "@/components/PhotoGallery.vue";
import { useProjectStore } from "@/stores/project";

const route = useRoute();
const id = computed(() => route.params.defectId as string);
const queryClient = useQueryClient();
const { selectedProjectId } = storeToRefs(useProjectStore());

const { data, isPending, isError, error } = useQuery({
  queryKey: ["defect", id],
  queryFn: () => getDefect(id.value),
});
const { data: history } = useQuery({
  queryKey: ["defect-history", id],
  queryFn: () => getDefectHistory(id.value),
});
const { data: workers } = useQuery({
  queryKey: ["workers", true],
  queryFn: () => listWorkers(true),
});
// App-enabled workers (those with a linked login) can action the assignment in
// the field app, so list them first and flag them in the picker.
const assignableWorkers = computed(() =>
  [...(workers.value ?? [])].sort(
    (a, b) =>
      (b.user_id ? 1 : 0) - (a.user_id ? 1 : 0) || a.name.localeCompare(b.name),
  ),
);

const busy = ref(false);
const actionError = ref<string | null>(null);

async function act(fn: () => Promise<void>): Promise<void> {
  busy.value = true;
  actionError.value = null;
  try {
    await fn();
    await queryClient.invalidateQueries({ queryKey: ["defect", id] });
    await queryClient.invalidateQueries({ queryKey: ["defect-history", id] });
    await queryClient.invalidateQueries({ queryKey: ["defects"] });
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

function onAssign(e: Event): void {
  const wid = (e.target as HTMLSelectElement).value;
  if (!wid) return;
  const w = (workers.value ?? []).find((x) => x.id === wid);
  void act(() => assignDefect(id.value, wid, w?.name));
}
function onPriority(e: Event): void {
  void act(() => updateDefect(id.value, { priority: (e.target as HTMLSelectElement).value }));
}
function onTargetDate(e: Event): void {
  void act(() => updateDefect(id.value, { target_date: (e.target as HTMLInputElement).value }));
}
function setStatus(status: "open" | "in_progress" | "closed"): void {
  void act(() => changeDefectStatus(id.value, status));
}
// --- Pre-close review + resolution draft (Phase 3) ---
const resolutionText = ref("");
const draftingResolution = ref(false);
async function runDraftResolution(): Promise<void> {
  draftingResolution.value = true;
  try {
    const r = await draftDefectResolution(id.value);
    resolutionText.value = r.draft;
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    draftingResolution.value = false;
  }
}
function resolveWithNotes(): void {
  void act(() => resolveDefect(id.value, resolutionText.value.trim() || undefined));
}

const reviewBusy = ref(false);
const reviewMsg = ref<string | null>(null);
const reviewFindings = ref<RegisterReviewFinding[] | null>(null);
async function runReview(): Promise<void> {
  reviewBusy.value = true;
  reviewMsg.value = null;
  try {
    const r = await reviewDefect(id.value);
    reviewFindings.value = r.findings;
    reviewMsg.value = r.ok
      ? "Looks complete — nothing flagged."
      : `${r.findings.length} thing(s) to check before closing.`;
  } catch (e) {
    reviewMsg.value = e instanceof Error ? e.message : String(e);
  } finally {
    reviewBusy.value = false;
  }
}
function toggleSnag(): void {
  void act(() => updateDefect(id.value, { is_snag: !data.value?.is_snag }));
}

// --- comments ---
const newComment = ref("");
async function addComment(): Promise<void> {
  const body = newComment.value.trim();
  if (!body) return;
  await act(() => addDefectComment(id.value, body));
  newComment.value = "";
}

// --- attachments ---
const uploading = ref(false);
async function onUploadAttachment(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) return;
  uploading.value = true;
  actionError.value = null;
  try {
    const fileId = await uploadFile(file, "issue-attachment");
    await act(() =>
      addDefectAttachment(id.value, {
        fileId,
        filename: file.name,
        mimeType: file.type || undefined,
      }),
    );
  } catch (err) {
    actionError.value = err instanceof Error ? err.message : String(err);
  } finally {
    uploading.value = false;
    input.value = "";
  }
}
function removeAttachment(fileId: string): void {
  void act(() => removeDefectAttachment(id.value, fileId));
}
async function openAttachment(fileId: string): Promise<void> {
  window.open(await getDownloadUrl(fileId), "_blank");
}

// --- watch ---
const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
// Clients get a read-only issue (view + export only).
const isClient = computed(() => (me.value?.roles ?? []).includes("client"));
const isWatching = computed(() =>
  (data.value?.watchers ?? []).some(
    (w) => (w as Record<string, unknown>).user_id === me.value?.id,
  ),
);
function toggleWatch(): void {
  if (isWatching.value) {
    void act(() => unwatchDefect(id.value));
  } else {
    void act(() =>
      watchDefect(id.value, { email: me.value?.email, name: me.value?.name }),
    );
  }
}

// --- structured location ---
const projectId = computed(
  () => (route.params.projectId as string) ?? data.value?.project_id ?? "",
);
const { data: locations } = useQuery({
  queryKey: ["locations", projectId],
  queryFn: () => listLocations(projectId.value),
  enabled: () => !!projectId.value,
});
function onLocation(e: Event): void {
  const sid = (e.target as HTMLSelectElement).value;
  const loc = (locations.value ?? []).find((l) => l.id === sid);
  void act(() =>
    updateDefect(id.value, {
      structure_id: sid,
      location_ref: loc ? loc.ref : "",
    }),
  );
}

// --- related issues ---
const { data: boardIssues } = useQuery({
  queryKey: ["project-issues", projectId],
  queryFn: () => getProjectIssues(projectId.value),
  enabled: () => !!projectId.value,
});
const linkTarget = ref("");
const linkRelation = ref("relates");
// Issues that can be linked: editable (defect-backed), not self, not already linked.
const linkableIssues = computed(() => {
  const linked = new Set((data.value?.related ?? []).map((r) => r.related_id));
  return (boardIssues.value?.issues ?? []).filter(
    (i) => i.editable && i.id !== id.value && !linked.has(i.id),
  );
});
const RELATION_LABEL: Record<string, string> = {
  relates: "relates to",
  blocks: "blocks",
  blocked_by: "blocked by",
  duplicate: "duplicate of",
};
async function addLink(): Promise<void> {
  if (!linkTarget.value) return;
  await act(() => linkDefect(id.value, linkTarget.value, linkRelation.value));
  linkTarget.value = "";
  linkRelation.value = "relates";
}
function removeLink(relatedId: string): void {
  void act(() => unlinkDefect(id.value, relatedId));
}

// --- export ---
const pdfBusy = ref(false);
async function downloadPdf(): Promise<void> {
  pdfBusy.value = true;
  actionError.value = null;
  try {
    const blob = await fetchDefectPdf(id.value);
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) {
    actionError.value = e instanceof Error ? e.message : String(e);
  } finally {
    pdfBusy.value = false;
  }
}

const fmtDate = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

const PRIORITIES = ["low", "medium", "high", "urgent"];
const riskClass = (r: string | null): string =>
  r === "high" ? "bg-semantic-danger/10 text-semantic-danger"
    : r === "medium" ? "bg-semantic-warning/10 text-semantic-warning"
      : "bg-neutral-100 text-neutral-500";
</script>

<template>
  <PageLayout>
    <RouterLink
      :to="{ name: 'project-issues', params: { projectId: $route.params.projectId } }"
      class="text-sm text-neutral-500 hover:text-neutral-900"
    >
      ← Issues
    </RouterLink>

    <div
      v-if="isPending"
      class="mt-4 text-neutral-500"
    >
      Loading…
    </div>
    <div
      v-else-if="isError"
      class="mt-4 rounded-md bg-semantic-danger/10 p-4 text-semantic-danger"
    >
      Failed to load: {{ error?.message }}
    </div>
    <div
      v-else-if="data"
      class="mt-4 grid gap-6"
    >
      <header class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-xl font-semibold capitalize">
            <span class="uppercase">{{ data.issue_type === "rfi" ? "RFI" : data.issue_type }}</span>
            <span class="font-normal text-neutral-400">·</span>
            {{ data.category }}
            <span
              class="ml-1 rounded-full px-2 py-0.5 align-middle text-xs capitalize"
              :class="riskClass(data.risk_rating)"
            >{{ data.risk_rating ?? "—" }}</span>
          </h1>
          <p class="mt-1 text-sm text-neutral-500">
            Raised {{ fmtDate(data.raised_at) }}
            <span v-if="data.raised_by_name"> by {{ data.raised_by_name }}</span>
            <span v-if="data.trade"> · {{ data.trade }}</span>
            <span v-if="data.source === 'check_sheet'"> · from an
              <RouterLink
                v-if="data.check_sheet_id"
                :to="{ name: 'check-sheet-detail', params: { projectId: selectedProjectId, id: data.check_sheet_id } }"
                class="text-brand-primary hover:underline"
              >ITP check sheet</RouterLink>
            </span>
          </p>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <button
            v-if="!isClient"
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="busy"
            @click="toggleWatch"
          >
            {{ isWatching ? "✓ Watching" : "Watch" }}
          </button>
          <button
            type="button"
            class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
            :disabled="pdfBusy"
            @click="downloadPdf"
          >
            {{ pdfBusy ? "…" : "PDF" }}
          </button>
          <span
            class="rounded-full px-3 py-1 text-xs font-medium capitalize"
            :class="data.status === 'closed' ? 'bg-semantic-success/10 text-semantic-success'
              : data.status === 'in_progress' ? 'bg-semantic-warning/10 text-semantic-warning'
                : 'bg-neutral-100 text-neutral-500'"
          >{{ data.status.replace("_", " ") }}</span>
        </div>
      </header>

      <p
        v-if="actionError"
        class="rounded-md bg-semantic-danger/10 p-3 text-sm text-semantic-danger"
      >
        {{ actionError }}
      </p>

      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-2 font-semibold">
          Description
        </h2>
        <p class="whitespace-pre-line text-sm text-neutral-800">
          {{ data.description }}
        </p>
        <p
          v-if="data.location_ref"
          class="mt-2 text-sm text-neutral-500"
        >
          📍 {{ data.location_ref }}
        </p>
        <p
          v-if="data.resolution_notes"
          class="mt-3 rounded-md bg-neutral-50 p-3 text-sm text-neutral-700"
        >
          <span class="font-medium">Resolution:</span> {{ data.resolution_notes }}
        </p>
      </section>

      <!-- Manage -->
      <section
        v-if="!isClient"
        class="rounded-xl border border-neutral-200 bg-neutral-0 p-6"
      >
        <h2 class="mb-4 font-semibold">
          Manage
        </h2>
        <div class="grid gap-4 sm:grid-cols-3">
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Assigned to</span>
            <select
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              :value="data.assigned_to ?? ''"
              :disabled="busy"
              @change="onAssign"
            >
              <option value="">
                Unassigned
              </option>
              <option
                v-for="w in assignableWorkers"
                :key="w.id"
                :value="w.id"
              >
                {{ w.name }}{{ w.user_id ? " — app user" : "" }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Priority</span>
            <select
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm capitalize"
              :value="data.priority ?? ''"
              :disabled="busy"
              @change="onPriority"
            >
              <option
                value=""
                disabled
              >
                Set…
              </option>
              <option
                v-for="p in PRIORITIES"
                :key="p"
                :value="p"
              >
                {{ p }}
              </option>
            </select>
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Target date</span>
            <input
              type="date"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              :value="data.target_date ?? ''"
              :disabled="busy"
              @change="onTargetDate"
            >
          </label>
          <label class="grid gap-1 text-sm">
            <span class="text-neutral-500">Location</span>
            <select
              v-if="(locations ?? []).length"
              class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
              :value="data.structure_id ?? ''"
              :disabled="busy"
              @change="onLocation"
            >
              <option value="">
                {{ data.location_ref || "Unspecified" }}
              </option>
              <option
                v-for="l in locations ?? []"
                :key="l.id"
                :value="l.id"
              >
                {{ l.ref }}{{ l.name ? ` — ${l.name}` : "" }}
              </option>
            </select>
            <span
              v-else
              class="px-1 py-2 text-neutral-500"
            >{{ data.location_ref || "—" }}
              <span class="text-xs text-neutral-400">(no locations imported)</span>
            </span>
          </label>
        </div>

        <div class="mt-5 flex flex-wrap items-center gap-2">
          <span class="text-sm text-neutral-500">Status:</span>
          <button
            v-for="s in (['open', 'in_progress', 'closed'] as const)"
            :key="s"
            type="button"
            class="rounded-md px-3 py-1.5 text-sm font-medium capitalize"
            :class="data.status === s
              ? 'bg-brand-primary text-brand-primary-on'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
            :disabled="busy"
            @click="setStatus(s)"
          >
            {{ s.replace("_", " ") }}
          </button>
        </div>

        <!-- Pre-close review + resolution (Phase 3) -->
        <div
          v-if="data.status !== 'closed'"
          class="mt-4 border-t border-neutral-100 pt-4"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="text-sm font-medium">Resolve & close</span>
            <button
              type="button"
              class="rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
              :disabled="reviewBusy"
              @click="runReview"
            >
              {{ reviewBusy ? "Checking…" : "Pre-close review" }}
            </button>
          </div>
          <p
            v-if="reviewMsg"
            class="mt-2 text-sm"
            :class="reviewFindings && reviewFindings.length
              ? 'text-neutral-700' : 'text-semantic-success'"
          >
            {{ reviewMsg }}
          </p>
          <ul
            v-if="reviewFindings && reviewFindings.length"
            class="mt-2 grid gap-1.5"
          >
            <li
              v-for="(f, i) in reviewFindings"
              :key="i"
              class="flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
              :class="f.severity === 'warning'
                ? 'border-semantic-warning/40 bg-semantic-warning/5'
                : 'border-neutral-200 bg-neutral-50'"
            >
              <span class="shrink-0">{{ f.severity === "warning" ? "⚠️" : "💡" }}</span>
              <span>{{ f.message }}</span>
            </li>
          </ul>
          <div class="mt-3 flex items-center justify-between">
            <label class="text-sm text-neutral-500">Resolution note</label>
            <button
              type="button"
              class="rounded-md border border-brand-primary/40 bg-brand-primary/5 px-2.5 py-1 text-xs font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
              :disabled="draftingResolution"
              @click="runDraftResolution"
            >
              {{ draftingResolution ? "Drafting…" : "✨ Draft" }}
            </button>
          </div>
          <textarea
            v-model="resolutionText"
            rows="3"
            placeholder="What was done to fix it (and how it was verified)…"
            class="mt-1 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
          <button
            type="button"
            class="mt-2 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-brand-primary-on hover:opacity-90 disabled:opacity-50"
            :disabled="busy"
            @click="resolveWithNotes"
          >
            Resolve & close
          </button>
        </div>

        <div class="mt-4 flex items-center gap-3 border-t border-neutral-100 pt-4">
          <button
            type="button"
            class="rounded-md px-3 py-1.5 text-sm font-medium"
            :class="data.is_snag
              ? 'bg-semantic-warning/15 text-semantic-warning'
              : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'"
            :disabled="busy"
            @click="toggleSnag"
          >
            {{ data.is_snag ? "✓ On snag list" : "Add to snag list" }}
          </button>
          <span class="text-xs text-neutral-400">
            Snags are the punch list cleared before project handover.
          </span>
        </div>
      </section>

      <!-- Photo evidence -->
      <PhotoGallery
        :file-ids="data.photo_ids"
        title="Photo evidence"
      />

      <!-- Attachments -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <div class="mb-3 flex items-center justify-between gap-3">
          <h2 class="font-semibold">
            Attachments ({{ data.attachments.length }})
          </h2>
          <label
            v-if="!isClient"
            class="cursor-pointer rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50"
            :class="{ 'pointer-events-none opacity-50': uploading }"
          >
            {{ uploading ? "Uploading…" : "+ Add file" }}
            <input
              type="file"
              class="hidden"
              :disabled="uploading"
              @change="onUploadAttachment"
            >
          </label>
        </div>
        <ul
          v-if="data.attachments.length"
          class="grid gap-2 text-sm"
        >
          <li
            v-for="a in data.attachments"
            :key="String(a.file_id)"
            class="flex items-center justify-between gap-3 rounded-md bg-neutral-50 px-3 py-2"
          >
            <button
              type="button"
              class="truncate text-left text-brand-primary hover:underline"
              @click="openAttachment(String(a.file_id))"
            >
              📎 {{ a.filename }}
            </button>
            <button
              v-if="!isClient"
              type="button"
              class="shrink-0 text-neutral-400 hover:text-semantic-danger"
              title="Remove"
              :disabled="busy"
              @click="removeAttachment(String(a.file_id))"
            >
              ✕
            </button>
          </li>
        </ul>
        <p
          v-else
          class="text-sm text-neutral-400"
        >
          No files attached.
        </p>
      </section>

      <!-- Comments -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          Comments ({{ data.comments.length }})
        </h2>
        <ul
          v-if="data.comments.length"
          class="mb-4 grid gap-3 text-sm"
        >
          <li
            v-for="(c, i) in data.comments"
            :key="i"
            class="rounded-md bg-neutral-50 p-3"
          >
            <p class="mb-1 text-xs text-neutral-400">
              {{ c.author_name ?? "—" }} · {{ fmtDate(c.at as string) }}
            </p>
            <p class="whitespace-pre-line text-neutral-800">
              {{ c.body }}
            </p>
          </li>
        </ul>
        <div
          v-if="!isClient"
          class="grid gap-2"
        >
          <textarea
            v-model="newComment"
            rows="2"
            placeholder="Add a comment…"
            class="rounded-md border border-neutral-300 px-3 py-2 text-sm"
          />
          <div>
            <button
              type="button"
              class="rounded-md bg-brand-primary px-3 py-2 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
              :disabled="busy || !newComment.trim()"
              @click="addComment"
            >
              Comment
            </button>
          </div>
        </div>
      </section>

      <!-- Related issues -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          Related issues ({{ data.related.length }})
        </h2>
        <ul
          v-if="data.related.length"
          class="mb-4 grid gap-2 text-sm"
        >
          <li
            v-for="r in data.related"
            :key="String(r.related_id)"
            class="flex items-center justify-between gap-3 rounded-md bg-neutral-50 px-3 py-2"
          >
            <span class="min-w-0">
              <span class="text-neutral-500">{{ RELATION_LABEL[String(r.relation)] ?? r.relation }}</span>
              <RouterLink
                :to="{ name: 'defect-detail', params: { projectId: $route.params.projectId, defectId: String(r.related_id) } }"
                class="ml-1 text-brand-primary hover:underline"
              >
                <span class="uppercase">{{ r.issue_type === "rfi" ? "RFI" : r.issue_type }}</span>:
                {{ r.title }}
              </RouterLink>
              <span class="ml-1 text-xs text-neutral-400">· {{ String(r.status).replace("_", " ") }}</span>
            </span>
            <button
              v-if="!isClient"
              type="button"
              class="shrink-0 text-neutral-400 hover:text-semantic-danger"
              title="Unlink"
              :disabled="busy"
              @click="removeLink(String(r.related_id))"
            >
              ✕
            </button>
          </li>
        </ul>
        <p
          v-else
          class="mb-4 text-sm text-neutral-400"
        >
          No linked issues.
        </p>
        <div
          v-if="linkableIssues.length && !isClient"
          class="flex flex-wrap items-center gap-2"
        >
          <select
            v-model="linkRelation"
            class="rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
          >
            <option value="relates">
              Relates to
            </option>
            <option value="blocks">
              Blocks
            </option>
            <option value="duplicate">
              Duplicate of
            </option>
          </select>
          <select
            v-model="linkTarget"
            class="min-w-0 flex-1 rounded-md border border-neutral-300 px-2 py-1.5 text-sm"
          >
            <option value="">
              Select an issue…
            </option>
            <option
              v-for="i in linkableIssues"
              :key="i.id"
              :value="i.id"
            >
              {{ i.type === "rfi" ? "RFI" : i.type }}: {{ i.title }}
            </option>
          </select>
          <button
            type="button"
            class="rounded-md bg-brand-primary px-3 py-1.5 text-sm font-semibold text-brand-primary-on disabled:opacity-50"
            :disabled="busy || !linkTarget"
            @click="addLink"
          >
            Link
          </button>
        </div>
      </section>

      <!-- Record + history -->
      <section class="rounded-xl border border-neutral-200 bg-neutral-0 p-6">
        <h2 class="mb-3 font-semibold">
          Record
        </h2>
        <dl class="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
          <dt class="text-neutral-500">
            Assigned to
          </dt>
          <dd>{{ data.assigned_to_name ?? "Unassigned" }}</dd>
          <dt class="text-neutral-500">
            Target date
          </dt>
          <dd>{{ data.target_date ?? "—" }}</dd>
          <template v-if="data.resolved_at">
            <dt class="text-neutral-500">
              Resolved
            </dt>
            <dd>{{ fmtDate(data.resolved_at) }}<span v-if="data.resolved_by_name"> · {{ data.resolved_by_name }}</span></dd>
          </template>
          <dt class="text-neutral-500">
            Last updated
          </dt>
          <dd>{{ fmtDate(data.last_updated_at) }}<span v-if="data.last_updated_by_name"> · {{ data.last_updated_by_name }}</span></dd>
        </dl>

        <h3 class="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-neutral-400">
          History
        </h3>
        <ol class="grid gap-1 text-sm">
          <li
            v-for="h in history ?? []"
            :key="h.seq"
            class="text-neutral-600"
          >
            <span class="flex flex-wrap gap-x-2">
              <span class="font-medium text-neutral-800">{{ h.label }}</span>
              <span class="text-neutral-400">·</span>
              <span>{{ h.actor_name ?? "—" }}</span>
              <span class="text-neutral-400">·</span>
              <span class="text-neutral-400">{{ fmtDate(h.at) }}</span>
            </span>
            <span
              v-if="h.detail"
              class="block pl-1 text-neutral-500"
            >“{{ h.detail }}”</span>
          </li>
        </ol>
      </section>
    </div>
  </PageLayout>
</template>
